<script lang="ts">
  import { formatIST } from '$lib/utils/dates';
  import { onDestroy } from 'svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Copy } from 'lucide-svelte';
  import { adminApiKeysApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { AdminApiKey } from '$lib/types';
  import { confirmAction } from '$lib/state/actionDialog.svelte';

  const queryClient = useQueryClient();
  const keysQuery = createQuery(() => ({
    queryKey: ['admin-api-keys'],
    queryFn: () => adminApiKeysApi.list()
  }));
  let showForm = $state(false);
  let name = $state('');
  let plaintext = $state('');
  let pending = $state(false);
  let error = $state('');
  let copyMessage = $state('');
  // Invalidates in-flight creation/copy callbacks on dismiss or unmount.
  let generation = 0;
  onDestroy(closeForm);

  function closeForm() {
    generation++;
    plaintext = '';
    name = '';
    error = '';
    copyMessage = '';
    showForm = false;
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (pending || !name.trim()) return;
    pending = true;
    error = '';
    const requestGeneration = generation;
    try {
      // Do not use createMutation: its cache would retain the plaintext response.
      const result = await adminApiKeysApi.create({ name: name.trim() });
      void queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] });
      if (generation === requestGeneration) plaintext = result.key;
    } catch {
      // Keep arbitrary server responses out of the one-time-secret UI.
      if (generation === requestGeneration) error = 'Could not create API key. Check the list before retrying.';
    } finally {
      pending = false;
    }
  }

  async function copyKey() {
    const requestGeneration = generation;
    try {
      await navigator.clipboard.writeText(plaintext);
      if (generation === requestGeneration) copyMessage = 'Copied';
    } catch {
      if (generation === requestGeneration) copyMessage = 'Copy failed. Select and copy the key manually.';
    }
  }

  const revokeMutation = createMutation(() => ({
    mutationFn: (id: number) => adminApiKeysApi.revoke(id),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] }); }
  }));

  async function revoke(key: AdminApiKey) {
    if (await confirmAction(`Revoke API key "${key.name}"? Any integration using it will stop working.`)) {
      revokeMutation.mutate(key.id);
    }
  }
</script>

<div>
  <div class="mb-[18px] flex items-center gap-3">
    <div>
      <h2 class="tym-section-title">API keys</h2>
      <div class="tym-section-sub">Read-only integrations &middot; keep keys private</div>
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" disabled={pending} onclick={() => { closeForm(); showForm = true; }}>
      <Plus class="h-4 w-4" /> New API key
    </button>
  </div>

  {#if revokeMutation.isError}<p class="tym-error-state">{getErrorMessage(revokeMutation.error)}</p>{/if}
  {#if keysQuery.isLoading}
    <p class="tym-loading-state">Loading API keys&hellip;</p>
  {:else if keysQuery.isError}
    <p class="tym-error-state">{getErrorMessage(keysQuery.error)}</p>
  {:else if keysQuery.data}
    <div class="tym-table-wrap">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead><tr><th>Name</th><th>Prefix</th><th>Created</th><th>Last used</th><th>Status</th><th class="num"></th></tr></thead>
          <tbody>
            {#each keysQuery.data as key (key.id)}
              <tr>
                <td class="font-semibold text-navy">{key.name}</td>
                <td><code>{key.prefix}</code></td>
                <td class="muted">{formatIST(key.created_at)}</td>
                <td class="muted">{key.last_used_at ? formatIST(key.last_used_at) : 'Never'}</td>
                <td>{key.revoked_at ? 'Revoked' : 'Active'}</td>
                <td class="num">
                  {#if !key.revoked_at}
                    <button type="button" class="btn-danger" disabled={revokeMutation.isPending} onclick={() => revoke(key)}>Revoke</button>
                  {/if}
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="6">No API keys yet.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title={plaintext ? 'API key created' : 'New API key'} onClose={closeForm}>
    {#if plaintext}
      <div class="space-y-4">
        <p class="text-sm font-semibold text-navy">Copy this key now — you won’t see this again.</p>
        <label class="label" for="new-api-key">API key</label>
        <textarea id="new-api-key" class="input font-mono" readonly value={plaintext} rows="3" spellcheck="false"></textarea>
        <button type="button" class="btn-secondary" onclick={copyKey}><Copy class="h-4 w-4" /> Copy key</button>
        <p class="text-sm" role="status">{copyMessage}</p>
        <div class="flex justify-end"><button type="button" class="btn-primary" onclick={closeForm}>Done</button></div>
      </div>
    {:else}
      <form class="space-y-4" onsubmit={submit}>
        <div>
          <label class="label" for="api-key-name">Name</label>
          <input id="api-key-name" class="input" bind:value={name} required disabled={pending} autocomplete="off" placeholder="Integration name" />
        </div>
        <p class="text-sm text-dark-gray">The key grants read-only API access. Its plaintext is shown only once.</p>
        {#if error}<p class="text-sm text-red-600" role="alert">{error}</p>{/if}
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
          <button type="submit" class="btn-primary" disabled={pending || !name.trim()}>{pending ? 'Creating...' : 'Create key'}</button>
        </div>
      </form>
    {/if}
  </Modal>
{/if}
